import { NextPageWithLayout } from 'next';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';
import ContentLoader from 'react-content-loader';

import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { AppStoreButtons } from '../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Container } from '../../components/Container/Container';
import { FAQ } from '../../components/FAQ/FAQ';
import { FindUSFishingSpot } from '../../components/FindUSFishingSpot/FindUSFishingSpot';
import { IAllStateWaterData } from '../../components/FindUSFishingSpot/helpers/buildStateDataWaters';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { QRCode } from '../../components/QRCode/QRCode';
import { Rating, RatingVariant } from '../../components/Rating/Rating';
import { SEOLinksFooter } from '../../components/SEOFooter/SEOLinksFooter';
import { Text } from '../../components/Text/Text';
import { IOS_APP_RATING } from '../../constants/constants';
import { ISEOWaterWithTypeList } from '../../constants/fishingWaters/constants';
import { USStateObject } from '../../constants/USstates/constants';
import { useClientHasRendered } from '../../helpers/hooks';
import { GetMostActiveWaters_statistics_mostActiveWatersLastWeek } from '../../interfaces/graphql';

import { faqData } from '../../components/FAQ/constants/constants';
import discoverBGDesktop from './assets/discoverBGDesktop.png';
import discoverBGMobile from './assets/discoverBGMobile.png';
import { OurReccomendedFishingSpots } from './components/MostPopularFishingLocations/OurReccomendedFishingSpots';
import { TopUSWatersByWaterType } from './components/TopUSWatersByWaterType/TopUSWatersByWaterType';

const FishingWaterCarousel = dynamic(async () =>
  import('../../components/FishingWaterCarousel/FishingWaterCarousel').then(
    mod => mod.FishingWaterCarousel,
  ),
);
export interface IDiscoverProps {
  lastWeekWaters?: GetMostActiveWaters_statistics_mostActiveWatersLastWeek[];
  stateWaterData: IAllStateWaterData;
  usStates: USStateObject[];
  defaultUSState: USStateObject;
  top20riversInUS: ISEOWaterWithTypeList;
  top20oceansInUS: ISEOWaterWithTypeList;
  top20lakesInUS: ISEOWaterWithTypeList;
}

const metaTemplate = {
  title: 'Explore fishing with Fishbrain ➡️️️️ Ultimate fishing directory',
  description:
    'Discover the best fishing spots, destinations, fishing reports, regulations and popular species ✅ Fishbrain Discover ➡ Your ultimate fishing directory • Register for Free!',
};

export const Discover: NextPageWithLayout<IDiscoverProps> = ({
  lastWeekWaters,
  stateWaterData,
  usStates,
  defaultUSState,
  top20riversInUS,
  top20oceansInUS,
  top20lakesInUS,
}) => {
  const clientHasRendered = useClientHasRendered();
  return (
    <div>
      <MetaTags
        title={metaTemplate.title}
        description={metaTemplate.description}
        image={discoverBGMobile.src}
        link="/discover"
      />
      <Container className="-px-6 sm:-px-11 md:-px-14 grid w-full grid-cols-1 grid-rows-1">
        <div className="col-start-1 row-start-1">
          <NextImage
            alt="most liked catch image"
            className="size-full object-cover sm:hidden"
            src={discoverBGMobile}
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <NextImage
            alt="most liked catch image"
            className="hidden size-full object-cover sm:block"
            src={discoverBGDesktop}
            placeholder="blur"
            sizes="(min-width: 1680px) 1552px, 100vw"
          />
        </div>
        <div className="col-start-1 row-start-1 flex flex-col items-center justify-center px-2 py-8 text-center sm:w-3/4 sm:pb-2 sm:pt-4 md:w-3/5 md:pt-8">
          <Heading as="h1" variant="lg" className="relative mb-4 mt-0 text-sea-foam">
            Explore fishing with Fishbrain
          </Heading>
          <Text className="px-12 text-sea-foam">
            Get the full experience by downloading the app
          </Text>
          {clientHasRendered ? (
            <div className="mt-3 flex flex-col md:flex-row md:gap-x-6">
              <QRCode source="Discover" />
              <div className="flex flex-col items-center">
                <div className="flex justify-start">
                  <AppStoreButtons source="Discover" orientation="vertical" />
                </div>
                <Rating
                  rating={IOS_APP_RATING}
                  variant={RatingVariant.allStars}
                  textSize="sm"
                  reviewPointColor="text-sea-foam"
                />
                <Text variant="sm" className="mt-2 font-semibold text-sea-foam">
                  37.6K ratings
                </Text>
              </div>
            </div>
          ) : (
            <ContentLoader
              uniqueKey="discover-species-rating-loader-key"
              speed={2}
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
              height={113}
              width={160}
            >
              <rect x="0" y="0" width="400" height="250" />
            </ContentLoader>
          )}
        </div>
      </Container>

      <TopUSWatersByWaterType
        top20riversInUS={top20riversInUS}
        top20oceansInUS={top20oceansInUS}
        top20lakesInUS={top20lakesInUS}
      />

      <FindUSFishingSpot
        stateWaterData={stateWaterData}
        usStates={usStates}
        defaultUSState={defaultUSState}
      />

      <OurReccomendedFishingSpots />

      {lastWeekWaters && lastWeekWaters.length > 0 && (
        <article className="pb-8">
          <FishingWaterCarousel
            dataTestId="last-week"
            title="Most Active Fishing spots (Last week)"
            clickEventSource="popular-last-week"
            waters={lastWeekWaters}
          />
        </article>
      )}
      {/* TODO: https://fishbrain.atlassian.net/browse/FIB-41168 */}
      <AppDownloadSection hasAppRating />
      <FAQ faqData={faqData} />
      <SEOLinksFooter />
    </div>
  );
};
